import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import _ from 'lodash';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Box, Button, Grid, Typography, TextField, Link } from '@mui/material';
import * as chanceApi from '../../../lib/api/chance';
import {
  chanceEstimateActive,
  chanceOrderCount,
  setChance,
} from '../../../modules/chanceModule/chance';
import { viewEstimate } from '../../../modules/chanceModule/chanceEstimate';
import AskDialog from '../../../components/common/dialog/AskDialog';
import DetailTemplate from '../../../components/common/template/DetailTemplate';
import CostCalcLockContainer from '../costcalc/CostCalcLockContainer';
import ForecastBaseInfoForm from '../../../components/common/base/ForecastBaseInfoForm';
import {getInitialFormValues} from '../../../components/common/base/ForecastBaseInfoInit';
import { validationForecast } from '../../../components/common/util/ForecastValid';

const ChanceEsitmateFormContainer = ({ stepperRef }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user, chance, estimate } = useSelector(({ chanceModule, auth }) => ({
    user: auth.user,
    chance: chanceModule.chance.chance,
    estimate: chanceModule.chanceEstimate.estimate,
  }));

  const [initialValues, setInitialValues] = useState(getInitialFormValues());

  useEffect(() => {
    if (chance) {
      dispatch(viewEstimate(chance.prjUuid));
      const baseLayout = getInitialFormValues(); 
      const merged = _.mergeWith({}, baseLayout, chance, (objValue, srcValue, key) => {

        if (key === 'invoiceToBe') {
          return _.mergeWith({}, baseLayout.invoiceToBe, srcValue || {}, (o, s) => s == null ? '' : undefined);
        }

        if (srcValue == null) return '';
        if (_.isDate(srcValue)) return srcValue;
        if (_.isArray(srcValue)) return [...srcValue];
      });

      setInitialValues(merged);
    }
  }, [chance, dispatch]);
  
  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationForecast(),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);
      formik.resetForm();
      setSubmitting(false);
      stepperRef.current?.scrollIntoView({ behavior: 'smooth' }); //저장 후 stepper로 이동함.
    },
  });

  const onSave = async (values) => {
    let dataToSubmit = {
      prjUuid: chance.prjUuid,
      cstNm: values.cstNm.trim(),
      prjTyp: values.prjTyp,
      prjImpt: values.prjImpt,
      bugetYn: values.bugetYn,
      forcastTxt: values.forcastTxt,
      procurement: values.procurement ? 'Y' : 'N',
      productYn: values.productYn,
      vendorCmp: values.vendorCmp,
      incClientTyp: values.incClientTyp,
      clientCmp: values.clientCmp,
      expDay: values.expDay ? dayjs(values.expDay).format('YYYY-MM-DD') : null,
      crtUsr: values.crtUsr, 
      partnerTyp: values.partnerTyp,
      salesTyp: values.salesTyp,
      procurement:values.procurement,
      customerCf:values.customerCf,
      ...(values.invoiceToBe?.contractStDt && {
        invoiceToBe: {
          prjUuid: chance.prjUuid,
          contractStDt: values.invoiceToBe.contractStDt ? dayjs(values.invoiceToBe.contractStDt).format('YYYY-MM-DD') : null,
          contractEdDt: values.invoiceToBe.contractStDt ? dayjs(values.invoiceToBe.contractEdDt).format('YYYY-MM-DD') : null,
          inspectYn: values.invoiceToBe.inspectYn,
          invTo: values.invoiceToBe.invTo,
          invMonth : values.invoiceToBe.invMonth,
          invDay: values.invoiceToBe.invDay,
          colTo: values.invoiceToBe.colTo,
          colDay: values.invoiceToBe.colDay,
          colMonth : values.invoiceToBe.colMonth,
          crtUsr: values.crtUsr,
          lockYn: values.invoiceToBe.lockYn,
          dateSchedules : values.invoiceToBe.dateSchedules,
        }       
      })
    };

    try {
      const res = await chanceApi.editChance(dataToSubmit);
      if (res.data) {
        
        dispatch(setChance(res.data.info));
        formik.setValues({
          ...formik.values, // 기존 입력값 유지 (필요 시)
          ...res.data.info  // 서버에서 넘겨준 최신 객체로 덮어쓰기
        });
        enqueueSnackbar(`세부 저장 성공`, {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  //원가산출 확정/ 해제

  const [unLockVisible, setUnlockVisible] = useState(false);
  const onUnLockCancle = () => {
    setUnlockVisible(false);
  };
  const onUnLockConfirm = () => {
    setUnlockVisible(false);
    onCostUnLock();
  };
  const onCostUnLock = async () => {
    try {
      const res = await chanceApi.unDoCost(chance.prjUuid);

      if (res.data.info) {
        formik.setValues({
          ...res.data.info  // 서버에서 넘겨준 최신 객체로 덮어쓰기
       });
        dispatch(setChance(res.data.info));
        dispatch(chanceEstimateActive(chance.prjUuid));
        dispatch(chanceOrderCount(chance.prjUuid));

        navigate(`/chancemng/${chance.prjUuid}/2/1`);

        enqueueSnackbar('원가산출이 해제되었습니다.', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 2,
        }}
      >
        <Button
          variant="contained"
          color="warning"
          onClick={() => setUnlockVisible(true)}
        >
          원가산출 해제
        </Button>
        <Button type="submit" variant="outlined" disabled={formik.isSubmitting}>
          저장
        </Button>
      </Box>
      {chance?.useYn === 'N' && (
        <DetailTemplate title="포캐스팅 실패 사유">
          <Box sx={{ whiteSpace: 'pre-line' }}>{chance?.failTxt}</Box>
        </DetailTemplate>
      )}
      <Box>
        {/* <ChanceEsitmateForm
          formik={formik}
          chance={chance}
          estimate={estimate}
        /> */}
        <ForecastBaseInfoForm formik={formik} chance={chance} />
      </Box>
      <DetailTemplate title="원가 산출 내역">
        <CostCalcLockContainer chance={chance} />
      </DetailTemplate>
      <DetailTemplate title="견적">
        {estimate ? (
          <>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적 번호
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.qteNo}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적일자
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.qteRegDt ? estimate?.qteRegDt : estimate?.crtDt}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적 유효일자
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.qteValidDt}
                </Typography>
              </Grid>
              <Grid item xs={4} />
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  담당자명
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.qteUsrNm}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  담당자 연락처
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.qteUsrTel}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  담당자 이메일
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.qteUsrEml}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  총 견적 금액
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  ₩{' '}
                  {estimate?.qtePrice
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  유지보수기간
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" component="div">
                  {estimate?.mntTor} 개월
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  납품 장소
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.destPlace}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  발행 조건
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.qteCondt}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  결제 조건
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.payCond}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적서 설명 1
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.qteDesc1}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적서 설명 2
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.qteDesc2}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Grid item xs={2}>
                <Typography variant="subtitle2" component="div">
                  견적서 설명 3
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2" component="div">
                  {estimate?.qteDesc3}
                </Typography>
              </Grid>
            </Grid>
          </>
        ) : (
          <Box>
            <Typography variant="subtitle2" component="div">
              현재 활성화된 견적서가 없습니다.
            </Typography>
          </Box>
        )}
      </DetailTemplate>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 2,
        }}
      >
        <Button
          variant="contained"
          color="warning"
          onClick={() => setUnlockVisible(true)}
        >
          원가산출 해제
        </Button>
        <Button type="submit" variant="outlined" disabled={formik.isSubmitting}>
          저장
        </Button>
      </Box>

      <AskDialog
        visible={unLockVisible}
        title="프로젝트 원가 산출 해제"
        description={`원가산출을 해제하시겠습니까?\r\n작업된(견적서 / 수주품의서 ) 등의 사항을 다시 작성해야됩니다.`}
        confirmText="해제"
        onConfirm={onUnLockConfirm}
        onCancle={onUnLockCancle}
      />
    </Box>
  );
};

export default ChanceEsitmateFormContainer;
